import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Icon } from "@iconify/react";
import { cn } from "lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";

import TermsAndConditions from "js/components/Auth/TermsAndConditions";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "js/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";
import { Separator } from "js/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "js/components/ui/tooltip";

import logoSvg from "../../assets/logo.svg";

const Navigation = ({
  isMobile,
  setCollapsed,
  collapsed = false,
}: {
  isMobile: boolean;
  setCollapsed: (value: boolean) => void;
  collapsed?: boolean;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userData } = useSelector((state: any) => state.entities.user);
  const { organizationData } = useSelector(
    (state: any) => state.entities.organization,
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [organizationOpen, setOrganizationOpen] = useState(false);

  const hasCreditHistModule =
    !!organizationData?.modules?.includes("credit_history");
  const hasTenantModule = !!organizationData?.modules?.includes("tenant");
  const isDev = import.meta.env.VITE_APP_ENV === "development";
  const hasContractAccess = !!(
    userData?.is_staff ||
    (userData?.user_permissions || []).some((p: string) =>
      p.startsWith("contracts."),
    )
  );

  const dashboardNavLabel = (() => {
    if (hasCreditHistModule && hasTenantModule) return "Reportes";
    if (hasCreditHistModule) return "Historial Crediticio";
    if (hasTenantModule) return "Solicitudes de Renta";
    return "";
  })();

  let hasOrganization = false;
  let isOwner = false;
  let allowAdvisorViewQuotes = false;
  let allowAdvisorViewMembers = false;
  let isApiMenuAvailable = false;

  if (userData != null && organizationData != null) {
    hasOrganization = true;
    isOwner = hasOrganization && userData?.role === "Owner";
    allowAdvisorViewQuotes = organizationData.visualize_quotes;
    allowAdvisorViewMembers = organizationData.visualize_members;
    isApiMenuAvailable =
      hasOrganization && organizationData.view_api_menu_options;
  }

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setCollapsed(true);
  };

  const matchPathname = (path: string, exact = false) => {
    if (!path) return false;
    if (path === "/") return pathname === "/";
    const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path;
    if (exact) return pathname === normalizedPath;
    return (
      pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`)
    );
  };

  const isPathActive = (
    targetPaths: string | string[],
    options?: { exact?: boolean },
  ) => {
    const list = Array.isArray(targetPaths) ? targetPaths : [targetPaths];
    return list.some((path) => matchPathname(path, options?.exact));
  };

  const organizationSectionPaths = useMemo(
    () => [
      "/organizations/members",
      "/organizations/credits",
      "/organizations/quotas",
      "/organizations/tenants",
      "/organizations/properties",
      "/organizations/reports",
      "/organizations/settings",
    ],
    [],
  );

  const organizationSectionActive = isPathActive(organizationSectionPaths);

  useEffect(() => {
    if (!collapsed && organizationSectionActive) {
      setOrganizationOpen(true);
    }
  }, [collapsed, organizationSectionActive]);

  const navButtonClass = ({
    active = false,
    danger = false,
    justifyCenter = false,
  }: {
    active?: boolean;
    danger?: boolean;
    justifyCenter?: boolean;
  }) =>
    cn(
      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
      justifyCenter ? "justify-center" : "justify-start",
      active
        ? "bg-primary/10 text-primary shadow-sm hover:bg-primary/10 hover:text-primary"
        : "hover:bg-accent hover:text-accent-foreground",
      danger && !active && "text-destructive hover:bg-destructive/10",
    );

  const NavItem = ({
    icon,
    label,
    onClick,
    danger = false,
    isActive = false,
  }: {
    icon: string;
    label: string;
    onClick: () => void;
    danger?: boolean;
    isActive?: boolean;
  }) => {
    const button = (
      <button
        onClick={onClick}
        className={navButtonClass({
          active: isActive,
          danger,
          justifyCenter: collapsed,
        })}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon icon={icon} className="h-5 w-5 flex-shrink-0" />
        {!collapsed && <span className="truncate">{label}</span>}
      </button>
    );

    if (collapsed) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="right">
              <p>{label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return button;
  };

  if (!userData) return null;

  return (
    <>
      <div className="flex h-full flex-col bg-background">
        <div
          className={cn(
            "flex items-center justify-center py-4",
            collapsed ? "px-2" : "px-4",
          )}
        >
          {collapsed ? (
            <img src={logoSvg} alt="Multiburó" className="h-8" />
          ) : (
            <img src={logoSvg} alt="Multiburó" className="h-16" />
          )}
        </div>

        <Separator />

        <nav
          className="flex-1 space-y-1 overflow-y-auto px-3 py-4"
          data-sentry-unmask
        >
          {!hasOrganization && (
            <NavItem
              icon="octicon:organization-24"
              label="Crear Organización"
              onClick={() => handleNavigation("/organizations/create")}
              danger
              isActive={isPathActive("/organizations/create")}
            />
          )}

          {/* Core Business Section */}
          {hasOrganization && (
            <>
              {!collapsed && (
                <div className="px-2 py-2">
                  <p className="nav-section-label text-xs font-medium text-muted-foreground">
                    Operaciones
                  </p>
                </div>
              )}

              <NavItem
                icon="icon-park-outline:table-report"
                label={dashboardNavLabel}
                onClick={() => handleNavigation("/")}
                isActive={isPathActive("/", { exact: true })}
              />

              {hasTenantModule && (
                <>
                  {hasContractAccess && isDev && (
                    <NavItem
                      icon="teenyicons:contract-outline"
                      label="Contratos"
                      onClick={() => handleNavigation("/contracts")}
                      isActive={isPathActive("/contracts")}
                    />
                  )}
                  <NavItem
                    icon="teenyicons:search-property-outline"
                    label="Inmuebles"
                    onClick={() => handleNavigation("/properties")}
                    isActive={isPathActive("/properties")}
                  />
                  <NavItem
                    icon="octicon:person-24"
                    label="Propietarios"
                    onClick={() => handleNavigation("/landlords")}
                    isActive={isPathActive("/landlords")}
                  />
                  <NavItem
                    icon="codicon:organization"
                    label="Inquilinos"
                    onClick={() => handleNavigation("/tenants")}
                    isActive={isPathActive("/tenants")}
                  />
                </>
              )}
            </>
          )}

          {/* Management Section */}
          {((hasOrganization &&
            (isOwner || allowAdvisorViewMembers || allowAdvisorViewQuotes)) ||
            (isOwner && hasTenantModule)) && (
            <>
              {collapsed ? (
                <Separator className="my-3" />
              ) : (
                <div className="px-2 py-2 mt-4">
                  <p className="nav-section-label text-xs font-medium text-muted-foreground">
                    Administración
                  </p>
                </div>
              )}
            </>
          )}

          {hasOrganization &&
            (isOwner || allowAdvisorViewMembers || allowAdvisorViewQuotes) &&
            (collapsed ? (
              <TooltipProvider delayDuration={0}>
                <Popover>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          className={navButtonClass({
                            active: organizationSectionActive,
                            justifyCenter: true,
                          })}
                        >
                          <Icon
                            icon="octicon:organization-24"
                            className="h-5 w-5 flex-shrink-0"
                          />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Organización</p>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent
                    side="right"
                    align="start"
                    className="w-56 p-2"
                  >
                    <div className="space-y-1">
                      {(isOwner || allowAdvisorViewMembers) && (
                        <button
                          onClick={() =>
                            handleNavigation("/organizations/members")
                          }
                          className={cn(
                            navButtonClass({
                              active: isPathActive("/organizations/members"),
                            }),
                            "py-2",
                          )}
                        >
                          <Icon icon="fluent-mdl2:people" className="h-4 w-4" />
                          <span>Asesores</span>
                        </button>
                      )}
                      {!(hasTenantModule && !hasCreditHistModule) &&
                        (isOwner || allowAdvisorViewQuotes) && (
                          <button
                            onClick={() =>
                              handleNavigation("/organizations/quotas")
                            }
                            className={cn(
                              navButtonClass({
                                active: isPathActive("/organizations/quotas"),
                              }),
                              "py-2",
                            )}
                          >
                            <Icon
                              icon="icon-park-outline:data"
                              className="h-4 w-4"
                            />
                            <span>Cuotas</span>
                          </button>
                        )}
                      {isOwner && (
                        <>
                          {hasTenantModule && (
                            <>
                              <button
                                onClick={() =>
                                  handleNavigation("/organizations/tenants")
                                }
                                className={cn(
                                  navButtonClass({
                                    active: isPathActive(
                                      "/organizations/tenants",
                                    ),
                                  }),
                                  "py-2",
                                )}
                              >
                                <Icon
                                  icon="codicon:organization"
                                  className="h-4 w-4"
                                />
                                <span>Inquilinos</span>
                              </button>
                              <button
                                onClick={() =>
                                  handleNavigation("/organizations/properties")
                                }
                                className={cn(
                                  navButtonClass({
                                    active: isPathActive(
                                      "/organizations/properties",
                                    ),
                                  }),
                                  "py-2",
                                )}
                              >
                                <Icon
                                  icon="uit:house-user"
                                  className="h-4 w-4"
                                />
                                <span>Inmuebles</span>
                              </button>
                            </>
                          )}
                          <button
                            onClick={() =>
                              handleNavigation("/organizations/reports")
                            }
                            className={cn(
                              navButtonClass({
                                active: isPathActive("/organizations/reports"),
                              }),
                              "py-2",
                            )}
                          >
                            <Icon
                              icon="icon-park-outline:table-report"
                              className="h-4 w-4"
                            />
                            <span>Reportes</span>
                          </button>
                          <button
                            onClick={() =>
                              handleNavigation("/organizations/settings")
                            }
                            className={cn(
                              navButtonClass({
                                active: isPathActive("/organizations/settings"),
                              }),
                              "py-2",
                            )}
                          >
                            <Icon
                              icon="heroicons:cog-6-tooth"
                              className="h-4 w-4"
                            />
                            <span>Configuración</span>
                          </button>
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </TooltipProvider>
            ) : (
              <Collapsible
                open={organizationOpen}
                onOpenChange={setOrganizationOpen}
              >
                <CollapsibleTrigger asChild>
                  <button
                    className={navButtonClass({
                      active: organizationSectionActive,
                    })}
                  >
                    <Icon
                      icon="octicon:organization-24"
                      className="h-5 w-5 flex-shrink-0"
                    />
                    <span className="flex-1 truncate text-left">
                      Organización
                    </span>
                    {organizationOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 pl-4">
                  {(isOwner || allowAdvisorViewMembers) && (
                    <NavItem
                      icon="fluent-mdl2:people"
                      label="Asesores"
                      onClick={() => handleNavigation("/organizations/members")}
                      isActive={isPathActive("/organizations/members")}
                    />
                  )}
                  {!(hasTenantModule && !hasCreditHistModule) &&
                    (isOwner || allowAdvisorViewQuotes) && (
                      <NavItem
                        icon="icon-park-outline:data"
                        label="Cuotas"
                        onClick={() =>
                          handleNavigation("/organizations/quotas")
                        }
                        isActive={isPathActive("/organizations/quotas")}
                      />
                    )}
                  {isOwner && (
                    <>
                      {hasTenantModule && (
                        <>
                          <NavItem
                            icon="codicon:organization"
                            label="Inquilinos"
                            onClick={() =>
                              handleNavigation("/organizations/tenants")
                            }
                            isActive={isPathActive("/organizations/tenants")}
                          />
                          <NavItem
                            icon="uit:house-user"
                            label="Inmuebles"
                            onClick={() =>
                              handleNavigation("/organizations/properties")
                            }
                            isActive={isPathActive("/organizations/properties")}
                          />
                        </>
                      )}
                      <NavItem
                        icon="icon-park-outline:table-report"
                        label="Reportes"
                        onClick={() =>
                          handleNavigation("/organizations/reports")
                        }
                        isActive={isPathActive("/organizations/reports")}
                      />
                      <NavItem
                        icon="heroicons:cog-6-tooth"
                        label="Configuración"
                        onClick={() =>
                          handleNavigation("/organizations/settings")
                        }
                        isActive={isPathActive("/organizations/settings")}
                      />
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>
            ))}

          {isOwner && hasTenantModule && (
            <NavItem
              icon="fa:id-card-o"
              label="Membresía"
              onClick={() => handleNavigation("/memberships/general")}
              isActive={isPathActive("/memberships")}
            />
          )}

          {/* Developer Section */}
          {isApiMenuAvailable && (
            <>
              {collapsed ? (
                <Separator className="my-3" />
              ) : (
                <div className="px-2 py-2 mt-4">
                  <p className="nav-section-label text-xs font-medium text-muted-foreground">
                    Desarrolladores
                  </p>
                </div>
              )}
              <NavItem
                icon="icon-park-outline:api"
                label="Integración API"
                onClick={() => handleNavigation("/developers")}
                isActive={isPathActive("/developers", { exact: true })}
              />
            </>
          )}
        </nav>

        {/* Help & Legal Section - Fixed at bottom */}
        <div className="border-t px-3 py-3 space-y-1" data-sentry-unmask>
          {collapsed ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() =>
                      window.open(
                        "https://healthy-soccer-73c.notion.site/Bienvenido-a-Multibur-Academy-1dc4ea49420a80469df8c1d86a4abcc3?pvs=4",
                        "_blank",
                      )
                    }
                    className="flex w-full items-center justify-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Icon
                      icon="mdi:learn-outline"
                      className="h-5 w-5 flex-shrink-0"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Tutoriales</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              onClick={() =>
                window.open(
                  "https://healthy-soccer-73c.notion.site/Bienvenido-a-Multibur-Academy-1dc4ea49420a80469df8c1d86a4abcc3?pvs=4",
                  "_blank",
                )
              }
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Icon
                icon="mdi:learn-outline"
                className="h-5 w-5 flex-shrink-0"
              />
              <span className="truncate">Tutoriales</span>
            </button>
          )}

          <NavItem
            icon="octicon:law-24"
            label="Términos y Condiciones"
            onClick={() => setModalVisible(true)}
          />
        </div>
      </div>

      <TermsAndConditions
        open={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </>
  );
};

export default Navigation;
